/**
 * Crops an image using an off-screen canvas and returns the result as a Blob.
 *
 * @param image - The loaded HTMLImageElement to crop
 * @param crop - The crop rectangle in pixel coordinates { x, y, width, height }
 * @param displayWidth - The displayed width of the image (for scaling to natural size).
 *                       If omitted, crop coords are assumed to be in natural-pixel space.
 * @param displayHeight - The displayed height of the image (for scaling to natural size).
 * @returns A Promise that resolves to the cropped image Blob (JPEG, quality 0.92)
 */
export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function cropImage(
  image: HTMLImageElement,
  crop: CropRect,
  displayWidth?: number,
  displayHeight?: number,
): Promise<Blob> {
  if ((displayWidth == null) !== (displayHeight == null)) {
    throw new Error(
      'Both displayWidth and displayHeight must be provided together',
    );
  }

  const scaleX = displayWidth ? image.naturalWidth / displayWidth : 1;
  const scaleY = displayHeight ? image.naturalHeight / displayHeight : 1;

  const pixelCrop = {
    x: crop.x * scaleX,
    y: crop.y * scaleY,
    width: crop.width * scaleX,
    height: crop.height * scaleY,
  };

  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context not available');
  }
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height,
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to crop image'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.92,
    );
  });
}
